import type { MockCatalogueItem } from "@/data/mockCatalogueItems";

const PLACEHOLDER_DOC_HOST = "docs.clarity.example.com";

/** Collapse whitespace; keep alphanumerics + common punctuation for search queries. */
function normalizeQueryPart(...parts: (string | null | undefined)[]): string {
  const s = parts
    .map((p) => String(p ?? "").trim())
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
  return s;
}

export function catalogueProductSearchCore(parts: {
  brand?: string | null;
  model?: string | null;
  displayName?: string | null;
}): string {
  const brand = normalizeQueryPart(parts.brand);
  const model = normalizeQueryPart(parts.model);
  const name = normalizeQueryPart(parts.displayName);
  return normalizeQueryPart(brand, model) || name || brand || "audio product";
}

export function isClarityPlaceholderDocumentUrl(url: string | null | undefined): boolean {
  const raw = String(url ?? "").trim();
  if (!raw) return false;
  try {
    return new URL(raw).hostname === PLACEHOLDER_DOC_HOST;
  } catch {
    return raw.includes(PLACEHOLDER_DOC_HOST);
  }
}

export type CatalogueDocumentIntent = "brochure" | "spec" | "review";

/**
 * Build a Google web search URL (new tab). Uses brand + model + display name — no placeholder doc URLs.
 */
export function buildCatalogueGoogleSearchUrl(
  item: Pick<MockCatalogueItem, "brand" | "model" | "displayName">,
  intent: CatalogueDocumentIntent
): string {
  return buildCatalogueGoogleSearchUrlFromParts(
    { brand: item.brand, model: item.model, displayName: item.displayName },
    intent
  );
}

export function buildCatalogueGoogleSearchUrlFromParts(
  parts: { brand?: string | null; model?: string | null; displayName?: string | null },
  intent: CatalogueDocumentIntent
): string {
  const core = catalogueProductSearchCore(parts);

  let q: string;
  if (intent === "brochure") {
    q = `${core} brochure`;
  } else if (intent === "spec") {
    q = `${core} spec sheet`;
  } else {
    q = `${core} review`;
  }

  const url = new URL("https://www.google.com/search");
  url.searchParams.set("q", q);
  return url.toString();
}

/**
 * Prefer an uploaded official document URL when present; otherwise Google search (default).
 */
export function resolveCatalogueDocumentUrl(
  item: Pick<MockCatalogueItem, "brand" | "model" | "displayName" | "brochureUrl" | "specSheetUrl" | "reviewsUrl">,
  intent: CatalogueDocumentIntent
): string {
  const stored =
    intent === "brochure"
      ? item.brochureUrl
      : intent === "spec"
        ? item.specSheetUrl
        : item.reviewsUrl;
  if (stored?.trim() && !isClarityPlaceholderDocumentUrl(stored)) {
    return stored.trim();
  }
  return buildCatalogueGoogleSearchUrl(item, intent);
}
